import type { Application } from "pixi.js";

/**
 * Calidad de render que se ajusta sola al ordenador que tiene delante.
 *
 * El problema medido: en un portatil flojo el juego "va a tirones". No es la
 * red — el lockstep sigue en hora — es que el frame tarda mas de 16 ms, asi que
 * el navegador baja el `requestAnimationFrame` a 30 fps y la fisica avanza a
 * saltos de dos ticks entre dibujo y dibujo. Se ve exactamente igual que lag, y
 * por eso se confunde con el.
 *
 * De donde sale el coste: Pixi arranca con `resolution = devicePixelRatio`
 * (topado a 2) y antialias. En una pantalla de retina eso son CUATRO veces los
 * pixeles de una normal para el mismo campo. Es lo correcto en un ordenador que
 * puede; en uno que no, es justo lo que le sobra.
 *
 * Lo que se toca y lo que NO:
 *
 * - **Se toca la resolucion de dibujo.** Es el unico ajuste que corta el trabajo
 *   por un factor grande sin quitar nada de la pantalla: se ve todo, un poco
 *   menos fino. Ningun elemento desaparece, asi que nadie pierde informacion de
 *   juego por tener un ordenador peor.
 * - **NO se toca nada que la simulacion pueda ver.** El lockstep exige que dos
 *   clientes simulen lo mismo tick a tick; un ajuste de calidad que cambiara
 *   fisica, IA o numero de jugadores seria un desync garantizado en la maquina
 *   mas lenta de la sala. Esto vive entero en el renderer.
 *
 * La histeresis (dos umbrales distintos para bajar y para subir) es
 * imprescindible: con un solo umbral, una maquina que rinde justo en la
 * frontera oscilaria entre niveles cada segundo, y un reescalado por segundo se
 * ve mucho peor que quedarse en el nivel bajo.
 */

/**
 * Escalones de resolucion, de mejor a peor.
 *
 * Se para en 0,6 y no baja mas: por debajo, el balon —que ya es de los objetos
 * mas pequeños en pantalla— empieza a perder forma, y un juego borroso que va
 * fluido no es mejor que uno nitido que va a tirones.
 */
const STEPS = [1, 0.75, 0.6] as const;

/** Media movil de ms por frame por encima de la cual se baja un escalon. */
const DROP_MS = 22;
/** Y por debajo de la cual se recupera. 13,5 ms ≈ 74 fps: margen de sobra. */
const RAISE_MS = 13.5;
/** Frames que hay que ver seguidos antes de mover nada. */
const SAMPLE_FRAMES = 90;

export interface AdaptiveQuality {
  /** Llamar una vez por frame, con el tiempo que costo el anterior. */
  sample(frameMs: number): void;
  /** ms por frame de la media movil, o -1 si aun no hay muestras. */
  readonly frameMs: number;
  /** Escalon actual, 1 = calidad completa. */
  readonly scale: number;
  dispose(): void;
}

export function createAdaptiveQuality(app: Application): AdaptiveQuality {
  /*
   * La resolucion de partida es el techo: en una pantalla normal es 1 y en una
   * de retina es 2. Los escalones se aplican SOBRE ella, no en absoluto, para
   * que "bajar un escalon" signifique lo mismo en las dos.
   */
  const base = app.renderer.resolution;
  let step = 0;
  let avg = -1;
  let frames = 0;

  const apply = (next: number): void => {
    if (next === step) return;
    step = next;
    const resolution = base * STEPS[step]!;
    // `resize` con las mismas dimensiones fuerza a Pixi a rehacer el buffer con
    // la resolucion nueva. Cambiar `resolution` a secas no lo reconstruye.
    app.renderer.resolution = resolution;
    app.renderer.resize(app.renderer.width, app.renderer.height);
    frames = 0;
    avg = -1;
  };

  return {
    sample(frameMs: number): void {
      // Un frame de 200 ms es casi siempre un parpadeo del sistema (cambio de
      // pestaña, compilacion de shader, recoleccion de basura). Bajar la calidad
      // por un pico aislado castigaria a maquinas que van perfectas.
      if (frameMs <= 0 || frameMs > 200) return;
      avg = avg < 0 ? frameMs : avg * 0.92 + frameMs * 0.08;
      frames += 1;
      if (frames < SAMPLE_FRAMES) return;

      if (avg > DROP_MS && step < STEPS.length - 1) apply(step + 1);
      else if (avg < RAISE_MS && step > 0) apply(step - 1);
      else frames = 0;
    },
    get frameMs(): number {
      return avg;
    },
    get scale(): number {
      return STEPS[step]!;
    },
    dispose(): void {
      if (step === 0) return;
      app.renderer.resolution = base;
      app.renderer.resize(app.renderer.width, app.renderer.height);
      step = 0;
    },
  };
}
